// src/pages/Solicitudes.jsx  (o donde tengas este componente)
import React, { useEffect, useMemo, useState } from "react"
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  query,
  where,
  getDoc,
} from "firebase/firestore"
import { getAuth } from "firebase/auth"
import emailjs from "@emailjs/browser"
import "../styles/solicitudesPages.css"

import { db } from "../firebaseconfig"
import SolicitudDetallesModal from "../components/modal/SolicitudDetallesModal"
import PDFSolicitudesExporter from "../components/reports/PDFSolicitudesExporter"
import logo2 from "../assets/logo2.png" // para el PDF del modal
import { Home, User, Car, Building2, Monitor, Eye, Check, X, Clock, FileText } from "lucide-react"

// ================= EmailJS =================
const EMAILJS_CONFIG = {
  serviceId: "service_yu1cttc",
  templateId: "template_5x8nxue",
  publicKey: "cu3_WWnIeDdkSmaKa",
}
emailjs.init(EMAILJS_CONFIG.publicKey)

const COLLECTION_MAP = {
  hipotecario: "creditosHipotecarios",
  personal: "creditosPersonales",
  prendario: "creditosPrendarios",
}
const getCollectionName = (tipo) => COLLECTION_MAP[tipo] || null

const SolicitudesPage = () => {
  const auth = getAuth()
  const user = auth.currentUser

  const [rol, setRol] = useState("")
  const [emailLogueado, setEmailLogueado] = useState("")
  const [loading, setLoading] = useState(true)

  const [solicitudes, setSolicitudes] = useState([])
  const [filteredSolicitudes, setFilteredSolicitudes] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedSolicitudes, setSelectedSolicitudes] = useState([])

  const [showActionMenu, setShowActionMenu] = useState(null)
  const [actionMenuCoords, setActionMenuCoords] = useState({ top: 0, left: 0 })
  const [showDetalles, setShowDetalles] = useState(null) // objeto solicitud seleccionado

  // ================= Rol + email del logueado =================
  useEffect(() => {
    const cargarRolYEmail = async () => {
      if (!user) return
      try {
        const userDocRef = doc(db, "users", user.uid)
        const userDocSnap = await getDoc(userDocRef)
        const rolLeido = userDocSnap.exists()
          ? (userDocSnap.data().rol || "").toLowerCase()
          : ""
        setRol(rolLeido)
        setEmailLogueado((user.email || "").toLowerCase())
      } catch (e) {
        console.error("No se pudo leer rol/email:", e)
        setRol("")
        setEmailLogueado("")
      }
    }
    cargarRolYEmail()
  }, [user])

  // ================= Cargar solicitudes (por rol) =================
  const cargarSolicitudes = async () => {
    try {
      setLoading(true)
      const todas = []

      const traer = async (nombreColeccion) => {
        const colRef = collection(db, nombreColeccion)
        const q = rol === "admin" ? colRef : query(colRef, where("email", "==", emailLogueado))
        const snap = await getDocs(q)
        snap.forEach((d) => {
          const data = d.data()
          todas.push({
            id: d.id,
            ...data,
            tipo:
              nombreColeccion === "creditosHipotecarios"
                ? "hipotecario"
                : nombreColeccion === "creditosPersonales"
                ? "personal"
                : "prendario",
          })
        })
      }

      await Promise.all([
        traer("creditosHipotecarios"),
        traer("creditosPersonales"),
        traer("creditosPrendarios"),
      ])

      // ordenar por fecha
      todas.sort((a, b) => new Date(b.fechaCreacion || 0) - new Date(a.fechaCreacion || 0))

      setSolicitudes(todas)
      setFilteredSolicitudes(todas) // se filtra abajo por aprobadas / search
    } catch (e) {
      console.error("Error cargando solicitudes:", e)
      alert("Error al cargar las solicitudes: " + (e.message || e))
    } finally {
      setLoading(false)
    }
  }

  // cargar cuando hay datos para filtrar
  useEffect(() => {
    if (!user) return
    if (!rol) return
    if (rol !== "admin" && !emailLogueado) return
    cargarSolicitudes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, rol, emailLogueado])

  // ================== Filtros (excluir aprobadas + search) ==================
  useEffect(() => {
    const filtered = solicitudes.filter(
      (s) =>
        s.estado !== "aprobada" &&
        (s.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.dni?.includes(searchTerm) ||
          s.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          s.email?.toLowerCase().includes(searchTerm.toLowerCase())),
    )
    setFilteredSolicitudes(filtered)
  }, [solicitudes, searchTerm])

  // ================== Helpers UI ==================
  const getEstadoBadge = (estado) => {
    const badges = {
      pendiente: { class: "badge-pendiente", text: "Pendiente" },
      aprobada: { class: "badge-aprobada", text: "Aprobada" },
      rechazada: { class: "badge-rechazada", text: "Rechazada" },
      revision: { class: "badge-revision", text: "En revisión" },
      faltaPago: { class: "badge-falta-pago", text: "Falta de pago" },
      faltapago: { class: "badge-falta-pago", text: "Falta de pago" },
    }
    const badge = badges[(estado || "").toString()] || badges.pendiente
    return <span className={`badge ${badge.class}`}>{badge.text}</span>
  }

  const getTipoBadge = (tipo) => {
    const badges = {
      hipotecario: { class: "tipo-hipotecario", text: "Hipotecario", icon: <Home size={13} style={{ marginRight: 4, flexShrink: 0 }} /> },
      personal:    { class: "tipo-personal",    text: "Personal",    icon: <User size={13} style={{ marginRight: 4, flexShrink: 0 }} /> },
      prendario:   { class: "tipo-prendario",   text: "Prendario",   icon: <Car  size={13} style={{ marginRight: 4, flexShrink: 0 }} /> },
    }
    const badge = badges[tipo] || badges.personal
    return (
      <span className={`tipo-badge ${badge.class}`} style={{ display: "inline-flex", alignItems: "center" }}>
        {badge.icon}{badge.text}
      </span>
    )
  }

  const formatCurrency = (n) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 0 }).format(n || 0)

  const formatDate = (d) => {
    try {
      return new Date(d).toLocaleDateString("es-AR")
    } catch {
      return "N/A"
    }
  }

  const getCargadoPor = (s) => {
    if (s.agenciaUsuario) {
      const nombre = s.agenciaUsuario.nombre || "Usuario no identificado"
      const esAgencia = s.agenciaUsuario.esAgencia
      const origen = s.origen
      if (origen === "simulador" && esAgencia) {
        return (
          <div className="cargado-por">
            <span className="cargado-icon"><Building2 size={14} /></span>
            <span className="cargado-nombre">{nombre}</span>
            <span className="cargado-tipo">Agencia</span>
          </div>
        )
      } else if (origen === "simulador") {
        return (
          <div className="cargado-por">
            <span className="cargado-icon"><Monitor size={14} /></span>
            <span className="cargado-nombre">{nombre}</span>
            <span className="cargado-tipo">Simulador</span>
          </div>
        )
      }
      return (
        <div className="cargado-por">
          <span className="cargado-icon"><User size={14} /></span>
          <span className="cargado-nombre">{nombre}</span>
          <span className="cargado-tipo">Usuario</span>
        </div>
      )
    }
    return (
      <div className="cargado-por">
        <span className="cargado-nombre">Formulario web</span>
        <span className="cargado-tipo">Directo</span>
      </div>
    )
  }

  // ================== Acciones (Firebase + mail) ==================
  const enviarEmail = async (solicitud, accion) => {
    try {
      const templateParams = {
        nombre_cliente: solicitud.nombre,
        email_cliente: solicitud.email,
        dni_cliente: solicitud.dni,
        tipo_credito: solicitud.tipo?.charAt(0).toUpperCase() + solicitud.tipo?.slice(1),
        monto_formateado: formatCurrency(solicitud.montoSolicitado),
        plazo: solicitud.plazo,
        estado_solicitud: accion,
        observaciones: solicitud.observaciones || "",
        motivo_rechazo: solicitud.motivoRechazo || "",
        es_aprobada: accion === "aprobada",
        es_rechazada: accion === "rechazada",
        es_revision: accion === "en revisión",
        es_faltadepago: accion === "falta de pago",
        empresa_nombre: "Gaaman servicios financieros",
        telefono_empresa: "0800-123-4567",
        email_empresa: "creditos@gaaman.com",
        web_empresa: "www.gaaman.com",
        to_email: solicitud.email,
        to_name: solicitud.nombre,
      }
      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        templateParams
      )
      console.log("Email enviado:", response)
      return true
    } catch (error) {
      console.error("Error enviando email:", error)
      alert("Error enviando email: " + (error.text || error.message))
      return false
    }
  }

  const actualizarEstado = async (solicitud, nuevoEstado, extra = {}) => {
    try {
      const col = getCollectionName(solicitud.tipo)
      if (!col) throw new Error(`Tipo desconocido: ${solicitud.tipo}`)
      const ref = doc(db, col, solicitud.id)
      const updateData = {
        estado: nuevoEstado,
        fechaActualizacion: new Date().toISOString(),
        ...extra,
        ...(nuevoEstado === "aprobada" && {
          fechaAprobacion: new Date().toISOString(),
          estadoPago: "nodebe",
        }),
      }
      await updateDoc(ref, updateData)
      setSolicitudes((prev) =>
        prev.map((x) => (x.id === solicitud.id ? { ...x, ...updateData } : x))
      )
      return true
    } catch (e) {
      console.error("Error actualizando estado:", e)
      return false
    }
  }

  const aprobarSolicitud = async (s) => {
    const ok = window.confirm(
      `¿Aprobar la solicitud de ${s.nombre}?\n\n` +
        `Tipo: ${s.tipo}\nMonto: ${formatCurrency(s.montoSolicitado)}\nEmail: ${s.email}`
    )
    if (!ok) return
    setLoading(true)
    try {
      const okUpd = await actualizarEstado(s, "aprobada")
      if (!okUpd) throw new Error("No se pudo actualizar en Firebase")
      const emailOk = await enviarEmail(s, "aprobada")
      alert(
        `✅ Solicitud ${s.id.substring(0, 8)} aprobada.\n` +
          (emailOk ? `📧 Email enviado a ${s.email}` : "⚠️ Email no enviado")
      )
    } catch (e) {
      alert("❌ Error aprobando solicitud: " + e.message)
    } finally {
      setLoading(false)
      setShowActionMenu(null)
    }
  }

  const rechazarSolicitud = async (s) => {
    const motivo = prompt("Motivo del rechazo (se enviará al cliente):")
    if (motivo === null) return
    const ok = window.confirm(`Confirmar rechazo y notificar a ${s.email}?`)
    if (!ok) return
    setLoading(true)
    try {
      const okUpd = await actualizarEstado(s, "rechazada", { motivoRechazo: motivo })
      if (!okUpd) throw new Error("No se pudo actualizar en Firebase")
      const emailOk = await enviarEmail({ ...s, motivoRechazo: motivo }, "rechazada")
      alert(
        `❌ Solicitud ${s.id.substring(0, 8)} rechazada.\n` +
          (emailOk ? `📧 Email enviado a ${s.email}` : "⚠️ Email no enviado")
      )
    } catch (e) {
      alert("❌ Error rechazando: " + e.message)
    } finally {
      setLoading(false)
      setShowActionMenu(null)
    }
  }

  const marcarEnRevision = async (s) => {
    const obs = prompt("Observaciones (opcional):")
    setLoading(true)
    try {
      const okUpd = await actualizarEstado(s, "revision", obs ? { observacionRevision: obs } : {})
      if (!okUpd) throw new Error("No se pudo actualizar en Firebase")
      const emailOk = await enviarEmail(s, "en revisión")
      alert(
        `⏳ Solicitud ${s.id.substring(0, 8)} en revisión.\n` +
          (emailOk ? `📧 Email enviado a ${s.email}` : "⚠️ Email no enviado")
      )
    } catch (e) {
      alert("❌ Error marcando revisión: " + e.message)
    } finally {
      setLoading(false)
      setShowActionMenu(null)
    }
  }

  // ===== callbacks para el modal (admin) =====
  const onMarcarPagado = async (credito) => {
    try {
      const col = getCollectionName(credito.tipo)
      const ref = doc(db, col, credito.id)
      await updateDoc(ref, { estadoPago: "nodebe", fechaPagoActualizado: new Date().toISOString() })
      setSolicitudes((prev) =>
        prev.map((x) => (x.id === credito.id ? { ...x, estadoPago: "nodebe" } : x))
      )
      alert("✅ Marcado como que no debe nada.")
    } catch (e) {
      console.error(e)
      alert("❌ Error actualizando estado de pago.")
    }
  }

  const onMarcarFaltaPago = async (credito) => {
    try {
      const col = getCollectionName(credito.tipo)
      const ref = doc(db, col, credito.id)
      await updateDoc(ref, { estadoPago: "faltapago", fechaPagoActualizado: new Date().toISOString() })
      setSolicitudes((prev) =>
        prev.map((x) => (x.id === credito.id ? { ...x, estadoPago: "faltapago" } : x))
      )
      // notificación opcional
      await enviarEmail(credito, "falta de pago")
      alert("⏰ Marcado con falta de pago y notificado al cliente.")
    } catch (e) {
      console.error(e)
      alert("❌ Error marcando falta de pago.")
    }
  };
  
  // ===== generador de PDF para pasar al modal (idéntico al que pediste) =====
   const onGenerarPDF = async (solicitud) => {
    const { default: jsPDF } = await import("jspdf")

    const comentarios = Array.isArray(solicitud.comentarios) ? solicitud.comentarios : []
    const incluirComentarios =
      comentarios.length > 0
        ? window.confirm(
            `¿Desea incluir los comentarios internos en el PDF?\n\nSe encontraron ${comentarios.length} comentario(s) interno(s).`
          )
        : false

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.width
    const pageHeight = doc.internal.pageSize.height
    let yPosition = 30

    const primaryColor = [0, 50, 38] // #003226
    const secondaryColor = [219, 197, 168] // #DBC5A8
    const grayColor = [107, 114, 128]
    const lightGray = [243, 244, 246]

    const checkNewPage = (neededSpace = 20) => {
      if (yPosition + neededSpace > pageHeight - 30) {
        doc.addPage()
        yPosition = 30
        return true
      }
      return false
    }

    // Header
    doc.setFillColor(...primaryColor)
    doc.rect(0, 0, pageWidth, 45, "F")

    const leftMargin = 20
    const logoWidth = 50
    const logoHeight = 20

    // Logo arriba izq
    // IMPORTANTE: jsPDF usa <img> en base64 o ruta importada por bundler; con Vite el import de PNG funciona.
    doc.addImage(logo2, "PNG", leftMargin, 8, logoWidth, logoHeight)

    // línea bajo logo
    doc.setDrawColor(...secondaryColor)
    doc.setLineWidth(0.5)
    doc.line(leftMargin, 8 + logoHeight + 2, leftMargin + logoWidth, 8 + logoHeight + 2)

    // texto header
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(12)
    doc.setFont(undefined, "normal")
    doc.text("Reporte de Solicitud de Crédito", leftMargin, 8 + logoHeight + 10)

    // fecha
    const fechaReporte = new Date().toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    doc.setFontSize(10)
    doc.text(`Generado: ${fechaReporte}`, pageWidth - 60, 38)

    yPosition = 60

    // badge estado
    const getEstadoColor = (estado) => {
      switch ((estado || "").toLowerCase()) {
        case "aprobada":
          return [16, 185, 129]
        case "rechazada":
          return [239, 68, 68]
        case "revision":
          return [245, 158, 11]
        default:
          return [59, 130, 246]
      }
    }
    const estadoColor = getEstadoColor(solicitud.estado)
    doc.setFillColor(...estadoColor)
    doc.roundedRect(pageWidth - 80, yPosition - 10, 60, 15, 3, 3, "F")
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(10)
    doc.setFont(undefined, "bold")
    doc.text((solicitud.estado || "pendiente").toUpperCase(), pageWidth - 75, yPosition)

    // reset
    doc.setTextColor(0, 0, 0)
    yPosition += 20

    const createSection = (title, content, bgColor = lightGray) => {
      checkNewPage(40)
      doc.setFillColor(...bgColor)
      doc.rect(20, yPosition - 8, pageWidth - 40, 20, "F")
      doc.setTextColor(...primaryColor)
      doc.setFontSize(14)
      doc.setFont(undefined, "bold")
      doc.text(title, 25, yPosition + 5)
      yPosition += 25

      doc.setTextColor(0, 0, 0)
      doc.setFontSize(11)
      doc.setFont(undefined, "normal")
      content.forEach((item) => {
        checkNewPage()
        if (item.label && item.value !== undefined) {
          doc.setFont(undefined, "bold")
          doc.text(`${item.label}:`, 25, yPosition)
          doc.setFont(undefined, "normal")
          doc.text(String(item.value), 80, yPosition)
        } else if (item.text) {
          const lines = doc.splitTextToSize(item.text, pageWidth - 50)
          lines.forEach((line) => {
            checkNewPage()
            doc.text(line, 25, yPosition)
            yPosition += 6
          })
          return
        }
        yPosition += 8
      })
      yPosition += 10
    }

    // ID
    doc.setFontSize(16)
    doc.setFont(undefined, "bold")
    doc.setTextColor(...primaryColor)
    doc.text(`ID: ${solicitud.id}`, 20, yPosition)
    yPosition += 20

    // info personal
    createSection(" INFORMACIÓN PERSONAL", [
      { label: "Nombre Completo", value: solicitud.nombre },
      { label: "DNI", value: solicitud.dni },
      { label: "Email", value: solicitud.email },
      { label: "Teléfono", value: solicitud.telefono },
      { label: "Ingresos Mensuales", value: formatCurrency(solicitud.ingresos) },
    ])

    // info crédito
    const creditoInfo = [
      {
        label: "Tipo de Crédito",
        value: solicitud.tipo?.charAt(0).toUpperCase() + solicitud.tipo?.slice(1),
      },
      { label: "Monto Solicitado", value: formatCurrency(solicitud.montoSolicitado) },
      {
        label: "Plazo",
        value: `${solicitud.plazo} ${solicitud.tipo === "hipotecario" ? "años" : "meses"}`,
      },
      { label: "Fecha de Solicitud", value: formatDate(solicitud.fechaCreacion) },
    ]
    if (solicitud.cuotaCalculada) {
      creditoInfo.push(
        { label: "Cuota Calculada", value: formatCurrency(solicitud.cuotaCalculada) },
        { label: "Total a Pagar", value: formatCurrency(solicitud.totalAPagar || 0) },
        { label: "Tasa Anual", value: `${solicitud.tasaAnual}%` },
      )
    }
    createSection("DETALLES DEL CRÉDITO", creditoInfo)

    // Observaciones
    if (solicitud.observaciones && String(solicitud.observaciones).trim()) {
      createSection(" OBSERVACIONES", [{ text: solicitud.observaciones }])
    }

    // comentarios (si aceptó)
    if (incluirComentarios && comentarios.length > 0) {
      createSection(" HISTORIAL DE COMENTARIOS", [])
      comentarios.forEach((comentario, index) => {
        checkNewPage(30)
        doc.setFillColor(...secondaryColor)
        doc.circle(30, yPosition, 8, "F")
        doc.setTextColor(255, 255, 255)
        doc.setFontSize(10)
        doc.setFont(undefined, "bold")
        doc.text(String(index + 1), 27, yPosition + 3)
        doc.setTextColor(...grayColor)
        doc.setFontSize(9)
        doc.setFont(undefined, "normal")
        const fecha = comentario.fecha ? formatDate(comentario.fecha) : ""
        doc.text(fecha, 45, yPosition + 3)
        yPosition += 15
        doc.setTextColor(0, 0, 0)
        doc.setFontSize(10)
        const comentarioLines = doc.splitTextToSize(String(comentario.texto || ""), pageWidth - 60)
        comentarioLines.forEach((line) => {
          checkNewPage()
          doc.text(line, 25, yPosition)
          yPosition += 5
        })
        if (comentario.archivos?.length > 0) {
          yPosition += 5
          doc.setTextColor(...primaryColor)
          doc.setFontSize(9)
          doc.text(`📎 ${comentario.archivos.length} archivo(s) adjunto(s)`, 25, yPosition)
        }
        yPosition += 15
      })
    }

    // footer
    const totalPages = doc.internal.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setDrawColor(...primaryColor)
      doc.setLineWidth(0.5)
      doc.line(20, pageHeight - 25, pageWidth - 20, pageHeight - 25)
      doc.setTextColor(...grayColor)
      doc.setFontSize(8)
      doc.setFont(undefined, "normal")
      doc.text("Financiera Gamaan - Reporte Confidencial", 20, pageHeight - 15)
      doc.text(`Página ${i} de ${totalPages}`, pageWidth - 40, pageHeight - 15)
    }

    const tipoCredito = solicitud.tipo?.charAt(0).toUpperCase() + solicitud.tipo?.slice(1)
    const fechaCorta = new Date().toISOString().split("T")[0]
    doc.save(`Solicitud_${tipoCredito}_${solicitud.id.substring(0, 8)}_${fechaCorta}.pdf`)
  }
  
  // ================== UI ==================
  if (loading) {
    return (
      <div className="solicitudes-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando solicitudes desde Firebase...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="solicitudes-page">
      <div className="page-header">
        <div className="header-actions">
          <button className="refresh-button" onClick={cargarSolicitudes}>
            Actualizar
          </button>
        </div>
      </div>

      <div className="search-container">
        <input
          type="text"
          placeholder="Buscar por nombre, DNI, ID o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        {filteredSolicitudes.length > 0 && (
          <span className="search-results">
            {filteredSolicitudes.length} solicitud{filteredSolicitudes.length !== 1 ? "es" : ""} encontrada
            {filteredSolicitudes.length !== 1 ? "s" : ""}
          </span>
        )}
        {rol === "admin" && <PDFSolicitudesExporter data={filteredSolicitudes} />}
      </div>

      <div className="table-container">
        <table className="solicitudes-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedSolicitudes.length === filteredSolicitudes.length && filteredSolicitudes.length > 0}
                  onChange={() => {
                    if (selectedSolicitudes.length === filteredSolicitudes.length) {
                      setSelectedSolicitudes([])
                    } else {
                      setSelectedSolicitudes(filteredSolicitudes.map((s) => s.id))
                    }
                  }}
                />
              </th>
              <th>ID</th>
              <th>Tipo</th>
              <th>Nombre</th>
              <th>DNI</th>
              <th>Email</th>
              <th>Monto</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Cargado por</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredSolicitudes.map((s) => (
              <tr key={s.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedSolicitudes.includes(s.id)}
                    onChange={() =>
                      setSelectedSolicitudes((prev) =>
                        prev.includes(s.id) ? prev.filter((id) => id !== s.id) : [...prev, s.id]
                      )
                    }
                  />
                </td>
                <td className="id-cell">{s.id.substring(0, 8)}...</td>
                <td>{getTipoBadge(s.tipo)}</td>
                <td className="nombre-cell">{s.nombre}</td>
                <td>{s.dni}</td>
                <td className="email-cell">{s.email}</td>
                <td className="monto-cell">{formatCurrency(s.montoSolicitado)}</td>
                <td>{formatDate(s.fechaCreacion)}</td>
                <td>{getEstadoBadge(s.estado)}</td>
                <td className="cargado-cell">{getCargadoPor(s)}</td>
                <td className="acciones-cell">
                  <div className="acciones-container">
                    <button
                      className="acciones-button"
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect()
                        setActionMenuCoords({
                          top: rect.bottom,
                          left: Math.min(rect.left, window.innerWidth - 200),
                        })
                        setShowActionMenu(showActionMenu === s.id ? null : s.id)
                      }}
                    >
                      ⋯
                    </button>
                    {showActionMenu === s.id && (
                      <div
                        className="acciones-menu"
                        style={{
                          position: "fixed",
                          top: `${actionMenuCoords.top}px`,
                          left: `${actionMenuCoords.left}px`,
                          zIndex: 9999,
                          width: "200px",
                          maxWidth: "90vw",
                        }}
                      >
                        <button
                          onClick={() => {
                            setShowDetalles(s)
                            setShowActionMenu(null)
                          }}
                        >
                          <Eye size={14} style={{ marginRight: 6 }} /> Ver detalles
                        </button>

                        {rol === "admin" && (
                          <>
                            {s.estado !== "aprobada" && (
                              <button onClick={() => aprobarSolicitud(s)} className="aprobar-btn">
                                <Check size={14} style={{ marginRight: 6 }} /> Aprobar
                              </button>
                            )}
                            {s.estado !== "rechazada" && (
                              <button onClick={() => rechazarSolicitud(s)} className="rechazar-btn">
                                <X size={14} style={{ marginRight: 6 }} /> Rechazar
                              </button>
                            )}
                            {s.estado !== "revision" && (
                              <button onClick={() => marcarEnRevision(s)} className="revision-btn">
                                <Clock size={14} style={{ marginRight: 6 }} /> Marcar en revisión
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredSolicitudes.length === 0 && !loading && (
        <div className="empty-state">
          <div className="empty-icon"><FileText size={48} color="#9ca3af" /></div>
          <h3>No se encontraron solicitudes</h3>
          <p>No hay solicitudes que coincidan con tu búsqueda.</p>
        </div>
      )}

      {/* Modal componentizado */}
      {showDetalles && (
        <SolicitudDetallesModal
          solicitud={showDetalles}
          isAdmin={rol === "admin"}
          onClose={() => setShowDetalles(null)}
          onGenerarPDF={onGenerarPDF}
          onMarcarPagado={rol === "admin" ? onMarcarPagado : undefined}
          onMarcarFaltaPago={rol === "admin" ? onMarcarFaltaPago : undefined}
        />
      )}
    </div> 
  )
}

export default SolicitudesPage
